import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { bookingService, expertService } from '../api/services';
import useApi from '../hooks/useApi';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Video, Mic, MessageCircle, Clock, AlertCircle, FileText, ExternalLink, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_TABS = ['ALL', 'CONFIRMED', 'COMPLETED', 'CANCELLED'];
const sessionIcon = { VIDEO: Video, AUDIO: Mic, CHAT: MessageCircle };
const statusStyle = {
  CONFIRMED: 'bg-amber-50 text-amber-700 border-amber-200',
  COMPLETED: 'bg-blue-50 text-blue-700 border-blue-200',
  CANCELLED: 'bg-cream-100 text-forest-400 border-cream-200',
};

// ─── Review Modal ─────────────────────────────────────────────
const ReviewModal = ({ session, onClose, onDone }) => {
  const [rating,  setRating]  = useState(0);
  const [hover,   setHover]   = useState(0);
  const [review,  setReview]  = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!rating) return toast.error('Please select a rating');
    setLoading(true);
    try {
      await expertService.submitReview(session.id, { rating, review });
      toast.success('Review submitted! Thank you.');
      onDone();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not submit review');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-forest-900/60 backdrop-blur-sm" />
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-5"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-xl text-forest-900">Rate Your Session</h3>
          <button onClick={onClose} className="text-forest-400 hover:text-forest-700 text-xl leading-none">✕</button>
        </div>

        {/* Stars */}
        <div className="flex items-center justify-center gap-2">
          {[1, 2, 3, 4, 5].map(star => (
            <button
              key={star}
              onClick={() => setRating(star)}
              onMouseEnter={() => setHover(star)}
              onMouseLeave={() => setHover(0)}
              className="text-3xl transition-transform hover:scale-110"
            >
              <span className={(hover || rating) >= star ? 'text-amber-400' : 'text-cream-200'}>
                ★
              </span>
            </button>
          ))}
        </div>
        {rating > 0 && (
          <p className="text-center text-sm text-forest-500">
            {['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][rating]}
          </p>
        )}

        {/* Review text */}
        <textarea
          value={review}
          onChange={e => setReview(e.target.value)}
          placeholder="Share your experience (optional)..."
          rows={4}
          className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500 resize-none"
        />

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 text-sm py-2.5 rounded-full border border-cream-200 text-forest-600 hover:border-forest-400 transition-colors"
          >
            Skip
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !rating}
            className="flex-1 btn-primary justify-center text-sm py-2.5 disabled:opacity-60"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : 'Submit Review'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────
const ClientSessionsPage = () => {
  const [activeTab,     setActiveTab]     = useState('ALL');
  const [reviewSession, setReviewSession] = useState(null);

  const { data, loading, error, refetch } = useApi(
    () => bookingService.getAll({ page: 1, limit: 20, ...(activeTab !== 'ALL' ? { status: activeTab } : {}) }),
    [activeTab]
  );

  const bookings = Array.isArray(data?.bookings)     ? data.bookings
    : Array.isArray(data?.data?.bookings)             ? data.data.bookings
    : Array.isArray(data?.data)                       ? data.data
    : Array.isArray(data)                             ? data : [];

  const sessionBookings = bookings.filter(b => !!b.session?.id);

  return (
    <>
      {reviewSession && (
        <ReviewModal
          session={reviewSession}
          onClose={() => setReviewSession(null)}
          onDone={refetch}
        />
      )}

      <DashboardLayout title="My Sessions">
        <div className="max-w-3xl space-y-5">

          {/* Tabs */}
          <div className="flex gap-2 flex-wrap">
            {STATUS_TABS.map(t => (
              <button key={t} onClick={() => setActiveTab(t)}
                className={`text-xs font-medium px-4 py-2 rounded-full border transition-all ${activeTab === t ? 'bg-forest-800 text-cream-50 border-forest-800' : 'bg-white text-forest-600 border-cream-200 hover:border-forest-400'}`}>
                {t}
              </button>
            ))}
          </div>

          {error && (
            <div className="flex items-center gap-3 text-rust-500 bg-rust-50 border border-rust-200 rounded-xl px-5 py-4">
              <AlertCircle size={16} />
              <span className="text-sm">Could not load sessions.</span>
            </div>
          )}

          {!loading && sessionBookings.length === 0 && (
            <div className="text-center py-16 bg-white border border-cream-200 rounded-2xl text-forest-400">
              <Video size={36} className="mx-auto mb-3 opacity-30" />
              <p className="font-serif text-xl text-forest-600">No sessions yet</p>
              <p className="text-sm mt-2">Your sessions will appear here once a room has been created</p>
            </div>
          )}

          <div className="space-y-3">
            {loading ? Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-white border border-cream-200 rounded-2xl p-5 animate-pulse h-24" />
            )) : sessionBookings.map(booking => {
              const Icon       = sessionIcon[booking.sessionType] ?? Video;
              const expertName = booking.expertProfile?.user
                ? `${booking.expertProfile.user.firstName} ${booking.expertProfile.user.lastName}`.trim()
                : 'Expert';
              const date = booking.scheduledAt
                ? new Date(booking.scheduledAt).toLocaleString('en-GH', { dateStyle: 'medium', timeStyle: 'short' })
                : '—';

              const isCompleted  = booking.status === 'COMPLETED';
              const isLive       = booking.session?.status === 'LIVE';
              const sessionId    = booking.session?.id;
              const hasRecording = !!booking.session?.recordingUrl;
              const transcript   = booking.session?.transcript;
              const hasReview    = !!booking.session?.review;

              return (
                <div key={booking.id} className="bg-white border border-cream-200 rounded-2xl p-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-forest-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon size={17} className="text-forest-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                          <h4 className="font-sans font-semibold text-forest-900 text-sm">
                            {booking.sessionType} with {expertName}
                          </h4>
                          <div className="flex items-center gap-3 mt-1 text-xs text-forest-500">
                            <span>{date}</span>
                            {booking.duration && (
                              <span className="flex items-center gap-1">
                                <Clock size={10} /> {booking.duration} mins
                              </span>
                            )}
                          </div>
                          {transcript?.status && (
                            <span className="text-xs text-forest-400 mt-1 block">
                              Transcript: {transcript.status}
                            </span>
                          )}
                        </div>
                        <span className={`text-[10px] font-medium border px-2.5 py-1 rounded-full flex-shrink-0 ${statusStyle[booking.status] ?? ''}`}>
                          {booking.status}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 mt-4 pt-4 border-t border-cream-100 flex-wrap">

                    {/* Rejoin if live */}
                    {isLive && (
                      <Link to={`/sessions/${sessionId}`} className="btn-primary text-xs py-1.5 px-4">
                        Rejoin Session →
                      </Link>
                    )}

                    {/* View Recording & Transcript */}
                    {isCompleted && (
                      <Link
                        to={`/sessions/${sessionId}/recording`}
                        className="flex items-center gap-1.5 text-xs border border-blue-200 text-blue-600 hover:bg-blue-50 px-3 py-1.5 rounded-full transition-colors font-medium"
                      >
                        <ExternalLink size={12} />
                        {hasRecording ? 'View Recording & Transcript' : 'View Session Details'}
                      </Link>
                    )}

                    {/* Download transcript PDF */}
                    {isCompleted && transcript?.pdfUrl && (
                      <a href={transcript.pdfUrl} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1.5 text-xs border border-forest-200 text-forest-600 hover:bg-forest-50 px-3 py-1.5 rounded-full transition-colors font-medium">
                        <FileText size={12} /> Download Transcript
                      </a>
                    )}

                    {/* Recording still processing */}
                    {isCompleted && !hasRecording && (
                      <span className="text-xs text-forest-400 italic">⏳ Recording processing...</span>
                    )}

                    {/* Leave Review — only if completed and not yet reviewed */}
                    {isCompleted && !hasReview && (
                      <button
                        onClick={() => setReviewSession(booking.session)}
                        className="flex items-center gap-1.5 text-xs border border-amber-200 text-amber-600 hover:bg-amber-50 px-3 py-1.5 rounded-full transition-colors font-medium"
                      >
                        ★ Leave Review
                      </button>
                    )}

                    {/* Already reviewed */}
                    {isCompleted && hasReview && (
                      <span className="text-xs text-forest-400 flex items-center gap-1">
                        ★ {booking.session.review.rating}/5 Reviewed
                      </span>
                    )}

                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </DashboardLayout>
    </>
  );
};

export default ClientSessionsPage;